// Copyright (c) StrangeBee 2023

import { convertCustomFields, convertOldCustomFields } from '@Helpers/customField';
import {
    CaseTemplateController,
    CaseCustomField,
    CaseTemplateInput,
    CaseTemplateOutput,
    CustomFieldValueInput,
} from 'thehive-sdk';

export interface CaseTemplateEventList {
    onCreate: (caseTemplate: CaseTemplateInput) => Promise<any>;
    onEdit: (caseTemplate: Partial<CaseTemplateInput>, caseTemplateId: string) => Promise<any>;
    onLinkPageTemplates: (pageTemplateIds: string[], caseTemplateId: string) => Promise<any>;
    onDelete: (caseTemplateId: string) => void;
}

interface CaseTemplateEventListParams {
    locale: (label: string) => string;
    controller: CaseTemplateController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
}: CaseTemplateEventListParams): CaseTemplateEventList => ({
    onCreate: ({ customFields, ...caseTemplate }) =>
        controller
            .create<CaseTemplateInput, CaseTemplateOutput>({
                ...caseTemplate,
                displayName: caseTemplate.displayName === '' ? undefined : caseTemplate.displayName,
                titlePrefix: caseTemplate.titlePrefix === '' ? undefined : caseTemplate.titlePrefix,
                customFields: Array.isArray(customFields)
                    ? (convertCustomFields(customFields as CaseCustomField[]) as CustomFieldValueInput[])
                    : convertOldCustomFields(customFields),
                pageTemplateIds:
                    caseTemplate.pageTemplateIds ??
                    caseTemplate.extraData?.pageTemplateHeaders.reduce((acc: string[], cur: CaseTemplateOutput) => {
                        return [...acc, cur._id];
                    }, []),
            })
            .then((data) => {
                onSuccess(true, locale('notification.case-template.create'));
                return data;
            })
            .catch((err) => {
                onError(err);
                return err;
            }),
    onEdit: (caseTemplate, caseTemplateId) => {
        if (caseTemplate?.customFields) {
            caseTemplate.customFields = convertCustomFields(
                caseTemplate.customFields as CaseCustomField[],
            ) as CustomFieldValueInput[];
        }
        return controller
            .edit<Partial<CaseTemplateInput>, CaseTemplateOutput>(caseTemplate, caseTemplateId)
            .then((data) => {
                onSuccess(false, locale('notification.case-template.edit'));
                return data;
            })
            .catch(onError);
    },
    onLinkPageTemplates: (pageTemplateIds, caseTemplateId) => {
        return controller
            .linkPageTemplates(pageTemplateIds, caseTemplateId)
            .then((data) => {
                return data;
            })
            .catch(onError);
    },
    onDelete: (caseTemplateId): void => {
        controller
            .delete(caseTemplateId)
            .then(() => onSuccess(true, locale('notification.case-template.delete')))
            .catch(onError);
    },
});
